class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.hasShown = false;
    this.useCookie = this.dataset.useCookie;
    this.expiringDays = this.dataset.expiringDays || 1;
    this.productsCount = Number(this.dataset.productsCount) || 6;
    this.sectionId = this.dataset.sectionId;
    this.showOncePerSession = this.dataset.showOnceSession === 'true';
  }

  connectedCallback() {
    this._onMouseOut = this._handleMouseOut.bind(this);
    this._onScroll = this._handleScroll.bind(this);

    document.addEventListener('mouseout', this._onMouseOut);
    window.addEventListener('scroll', this._onScroll);

    this.loadRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('mouseout', this._onMouseOut);
    window.removeEventListener('scroll', this._onScroll);

    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = null;
    }
  }

  loadRecommendations() {
    const sectionId = this.dataset.sectionId;
    const carousel = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-carousel`);
    const swiperEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper`);
    const nextEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper-button-next`);
    const prevEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper-button-prev`);
    const popupEl = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-modal`);
    const closeEls = this.querySelectorAll(`.js-exit-intent--${sectionId} .js-exit-intent-close`);
    const overlayEl = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-overlay`);

    this.refactorCarousel(carousel);
    const productSlides = carousel?.querySelectorAll('.swiper-slide') || [];
    if (productSlides.length === 0) return;

    this.initSwiper(swiperEl, nextEl, prevEl, carousel);
    this.popupInit(popupEl, overlayEl, closeEls);
    this.setupExitIntent(popupEl, overlayEl);
  }

  refactorCarousel(carousel) {
    if (!carousel) return;

    const STORAGE_KEY = 'recentlyViewedProducts';
    const storedData = JSON.parse(localStorage.getItem(STORAGE_KEY));
    const products = storedData?.products || [];
    const currentViewedProductId = JSON.parse(localStorage.getItem('currentViewedProduct'));

    if (products.length > 0) {
      const carouselContent = carousel.innerHTML;
      carousel.innerHTML = '';

      const existingIds = new Set();

      for (const product of products) {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.setAttribute('data-product-id', product.id);
        slide.innerHTML = this.buildSwiperSlide(product);
        carousel.appendChild(slide);

        if (product.id) {
          existingIds.add(String(product.id));
        }
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(carouselContent, 'text/html');
      const bodyChildren = doc.body.children;

      for (const slide of bodyChildren) {
        const id = slide.getAttribute('data-product-id');
        if (id && !existingIds.has(String(id))) {
          carousel.appendChild(slide.cloneNode(true));
        }
      }
    }

    const slides = Array.from(carousel.querySelectorAll('.swiper-slide'));

    let renderedCount = 0;

    for (const slide of slides) {
      const id = slide.dataset.productId;

      if (currentViewedProductId && String(id) === String(currentViewedProductId)) {
        slide.remove();
        continue;
      }

      if (renderedCount >= this.productsCount) {
        slide.remove();
        continue;
      }

      renderedCount++;
    }
  }

  buildSwiperSlide(product) {
    const isOnSale = product.compare_at_price && product.compare_at_price !== product.price;

    return `
      <a href="${product.url}" class="exit-intent__link">
        <div class="exit-intent__image-wrapper">
          <img
            src="${product.featured_image}"
            alt="${product.title}"
            class="exit-intent__image"
            loading="lazy"
            width="207"
            height="275"
          />
        </div>
        <div class="exit-intent__content">
          <p class="exit-intent__title">${product.title}</p>
          <div class="exit-intent__price-wrapper">
            ${isOnSale
              ? `
                <span class="visually-hidden">Original price</span>
                <span class="exit-intent__price-original">${product.compare_at_price}</span>
                <span class="visually-hidden">Sale price</span>
                <span class="exit-intent__price-sale">${product.price}</span>
              `
              : `
                <span class="visually-hidden">Price</span>
                <span class="exit-intent__price">${product.price}</span>
              `}
          </div>
          <div class="yotpo-widget-instance" data-yotpo-instance-id="${window.yotpo_stars_instance_id}" data-yotpo-product-id="${product.id}" data-yotpo-cart-product-id="${product.id}" data-yotpo-section-id="${this.sectionId}"></div>
        </div>
      </a>
    `;
  }

  initSwiper(swiperEl, nextEl, prevEl, carousel) {
    if (!swiperEl || typeof Swiper === 'undefined') return;

    const swiperAmount = swiperEl.querySelectorAll('.swiper-slide').length;
    if (swiperAmount <= 0) {
      nextEl?.classList.add('visually-hidden');
      prevEl?.classList.add('visually-hidden');
      return;
    } else if (swiperAmount === 1) {
      carousel.classList.add('exit-intent__center-carousel-sm');
    } else if (swiperAmount === 2) {
      carousel.classList.add('exit-intent__center-carousel-lg');
    }

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 2,
      spaceBetween: 20,
      loop: false,
      navigation: { nextEl, prevEl },
      breakpoints: {
        1024: { slidesPerView: 3, spaceBetween: 30 },
      },
      on: {
        afterInit: function () {
          toggleNav(this);
        },
        resize: function () {
          toggleNav(this);
        }
      }
    });

    function toggleNav(swiper) {
      const visibleSlides = getSlidesPerView(swiper);
      const totalSlides = swiper.slides.length;

      if (totalSlides <= visibleSlides) {
        nextEl?.classList.add('visually-hidden');
        prevEl?.classList.add('visually-hidden');
      } else {
        nextEl?.classList.remove('visually-hidden');
        prevEl?.classList.remove('visually-hidden');
      }
    }

    function getSlidesPerView(swiper) {
      const breakpoints = swiper.params.breakpoints;
      const width = window.innerWidth;
      let slidesPerView = swiper.params.slidesPerView;

      if (breakpoints) {
        const matched = Object.keys(breakpoints)
          .map(n => Number(n))
          .sort((a, b) => b - a)
          .find(bp => width >= bp);

        if (matched) {
          slidesPerView = breakpoints[matched].slidesPerView;
        }
      }

      return typeof slidesPerView === 'number' ? slidesPerView : 1;
    }
  }

  popupInit(popupEl, overlayEl, closeEls) {
    if (!popupEl || closeEls.length === 0) return;
    closeEls.forEach((closeEl) => {
      closeEl.addEventListener('click', () => {
        popupEl.classList.remove('is-active');
        overlayEl.classList.remove('is-active');
      });
    });
  }

  showPopup(popupEl, overlayEl) {
    if (!popupEl || !overlayEl) return;
    popupEl.classList.add('is-active');
    overlayEl.classList.add('is-active');
  }

  setupExitIntent(popupEl, overlayEl) {
    if (
      this.getCookie('exitpopup') === 'hide' ||
      this.hasShown ||
      (this.showOncePerSession && sessionStorage.getItem('exitIntentShown') === 'true')
    ) {
      return;
    }

    this._lastScroll = window.pageYOffset;
    this._maxScroll = this._lastScroll;

    this._popupEl = popupEl;
    this._overlayEl = overlayEl;
  }

  _handleMouseOut(e) {
    const isLeaving = !e.toElement && !e.relatedTarget;
    if (isLeaving && !this.hasShown) {
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }
  }

  _handleScroll() {
    const currentScroll = window.pageYOffset;
    const delta = currentScroll - this._lastScroll;

    const deltaThreshold = window.innerWidth < 768 ? -30 : -50;
    if (delta < deltaThreshold && !this.hasShown) {
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }

    if (currentScroll > this._maxScroll) {
      this._maxScroll = currentScroll;
    }

    if (
      this._maxScroll > window.innerHeight * 2 &&
      currentScroll < this._maxScroll - window.innerHeight * 0.1 &&
      !this.hasShown
    ) {
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }

    this._lastScroll = currentScroll;
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this._lastScroll = currentScroll;
    }, 100);
  }

  triggerExitIntent(popupEl, overlayEl) {
    this.showPopup(popupEl, overlayEl);
    this.hasShown = true;

    if (this.useCookie === 'true') {
      this.setCookie('exitpopup', 'hide', this.expiringDays);
    }

    if (this.showOncePerSession) {
      sessionStorage.setItem('exitIntentShown', 'true');
    }
  }

  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      expires = '; expires=' + d.toGMTString();
    }
    document.cookie = `${name}=${value}${expires}; path=/`;
  }

  getCookie(name) {
    const prefix = name + '=';
    return document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(prefix))?.slice(prefix.length) || null;
  }
}

customElements.define('exit-intent-recommendations', ProductRecommendations);
